import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '../lib/utils'
import { ParsleyLogo } from './parsley-logo'
import { ParsleyMark } from './parsley-mark'
import { ParsleyWordmark } from './parsley-wordmark'

// The primary Parsley lockup — the mark beside the wordmark, the app's brand signature.
// One `size` scales the mark, wordmark, and gap together so the halves stay balanced; `tone`
// themes both for light vs. green/dark/photo surfaces. `variant` picks the emblem:
//   • badge (default) — the mark in a rounded square (the header / app-icon lockup).
//   • bare — the mark on its own, no square (footers, brand-over-photo, tight utility rows).
// Reach for this over hand-assembling a mark + text so the mark-to-word proportion stays fixed.
type LockupSize = 'sm' | 'md' | 'lg'
type LockupTone = 'default' | 'inverse'
type LockupVariant = 'badge' | 'bare'

const lockupVariants = cva('inline-flex items-center', {
  variants: {
    // Fixed px gaps (not rem) so the lockup spacing doesn't scale with the root clamp.
    size: {
      sm: 'gap-[8px]',
      md: 'gap-[10px]',
      lg: 'gap-[12px]'
    }
  },
  defaultVariants: {
    size: 'md'
  }
})

// Badge variant → the ParsleyLogo tile tone + the matching wordmark ink.
const BADGE_TONE = {
  // On light surfaces: green badge + dark-green wordmark (matches the header).
  default: { logo: 'green', word: 'dark-green' },
  // On green / dark / photo surfaces: white badge + white wordmark.
  inverse: { logo: 'white', word: 'white' }
} as const

// Bare variant is monochrome: one ink set on the wrapper as currentColor, which the mark
// (currentColor fill) and the wordmark (tone="current") both inherit. Pass a text-* class to
// override — a theme-adaptive tint, or a drop-shadow for legibility over a photo.
const BARE_INK = {
  default: 'text-brand-green-dark',
  inverse: 'text-white'
} as const

// Bare mark footprint per size — matched to the leaf the badge shows at the same size (the
// badge mark is ~half the tile), so switching badge ↔ bare keeps an identical proportion.
const BARE_MARK = {
  sm: 'size-[16px]',
  md: 'size-[20px]',
  lg: 'size-[28px]'
} as const

export interface ParsleyLockupProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: LockupSize
  tone?: LockupTone
  variant?: LockupVariant
}

export const ParsleyLockup = React.forwardRef<HTMLDivElement, ParsleyLockupProps>(
  ({ className, size = 'md', tone = 'default', variant = 'badge', ...props }, ref) => {
    if (variant === 'bare') {
      return (
        <div ref={ref} className={cn(lockupVariants({ size }), BARE_INK[tone], className)} {...props}>
          <ParsleyMark filled className={BARE_MARK[size]} />
          <ParsleyWordmark size={size} tone="current" />
        </div>
      )
    }
    return (
      <div ref={ref} className={cn(lockupVariants({ size }), className)} {...props}>
        <ParsleyLogo size={size} tone={BADGE_TONE[tone].logo} />
        <ParsleyWordmark size={size} tone={BADGE_TONE[tone].word} />
      </div>
    )
  }
)
ParsleyLockup.displayName = 'ParsleyLockup'

export { lockupVariants }
